import React from 'react';
import { cn } from '@/lib/cn';
import { Dropdown, DropdownOption } from '@/components/ui/Dropdown';
import { Picker } from '@/components/picker';
import { BottomSheet } from '@/components/ui';
import { useStore } from '@/providers/StoreProvider';
import { FilterActionBar } from './FilterActionBar';
import { usePriceFilter } from './hooks/usePriceFilter';

export interface PriceFilterProps {
  variant?: 'desktop' | 'mobile';
  className?: string;
  // Mobile-specific props
  open?: boolean;
  onClose?: () => void;
  requireConfirmation?: boolean;
  page?: 'listings' | 'home';
  shouldUpdateUrl?: boolean;
}

export function PriceFilter({
  variant = 'desktop',
  className,
  open,
  onClose,
  requireConfirmation = false,
  page = 'listings',
  shouldUpdateUrl = true,
}: PriceFilterProps) {
  const { price, minOptions, maxOptions, handleMinChange, handleMaxChange } = usePriceFilter();
  const setPrice = useStore(s => s.listingsFilters.setPrice);

  if (variant === 'mobile') {
    return (
      <BottomSheet
        label="Select price range"
        bodyClassName="py-8 px-6"
        open={open!}
        onClose={onClose!}
        leading={false}
        footer={
          requireConfirmation ? (
            <FilterActionBar
              shouldUpdateUrl={shouldUpdateUrl}
              scope="price"
              onApply={() => {
                onClose?.();
              }}
              {...(page === 'home' ? { primaryButtonText: 'Done' } : {})}
              onReset={() => {
                setPrice({ min: null, max: null });
              }}
            />
          ) : null
        }
      >
        <div className="flex items-center gap-4">
          <Picker
            name="minPrice"
            options={minOptions.map(opt => ({
              value: opt.value === null ? 'null' : opt.value,
              label: opt.label,
            }))}
            value={price.min === null ? 'null' : price.min}
            className="grow"
            onChange={v => handleMinChange(v === 'null' ? null : (v as number))}
            formatLabel={o => o.label ?? ''}
          />
          <span className="text-body-2 text-secondary">to</span>
          <Picker
            name="maxPrice"
            className="grow"
            options={maxOptions.map(opt => ({
              value: opt.value === null ? 'null' : opt.value,
              label: opt.label,
            }))}
            value={price.max === null ? 'null' : price.max}
            onChange={v => handleMaxChange(v === 'null' ? null : (v as number))}
            formatLabel={o => o.label ?? ''}
          />
        </div>
      </BottomSheet>
    );
  }

  return (
    <div className={cn(className)}>
      <div className={cn('flex items-start', 'gap-4')}>
        <div className={cn('flex flex-1 flex-col items-start', 'gap-1')}>
          <Dropdown
            options={minOptions as DropdownOption<number>[]}
            value={price.min}
            label="Min price"
            onChange={handleMinChange}
            matchTriggerWidth
          />
        </div>
        <div className={cn('flex flex-1 flex-col items-start', 'gap-1')}>
          <Dropdown
            options={maxOptions as DropdownOption<number>[]}
            value={price.max}
            label="Max price"
            onChange={handleMaxChange}
            matchTriggerWidth
          />
        </div>
      </div>
      {requireConfirmation && (
        <FilterActionBar
          scope="price"
          className="mt-4 !px-0 !py-0 !pt-4"
          shouldUpdateUrl={shouldUpdateUrl}
          onApply={() => {
            onClose?.();
          }}
          {...(page === 'home' ? { primaryButtonText: 'Done' } : {})}
          onReset={() => {
            setPrice({ min: null, max: null });
          }}
        />
      )}
    </div>
  );
}

export default PriceFilter;
